/**
 * useScrollSpy — generic IntersectionObserver-driven hook that tracks
 * which of a list of sections is currently the "active" one based on
 * scroll position, and exposes a smooth-scroll helper to navigate to
 * any of them by id.
 *
 * Built for Phase 4 of the pilot React-engine port (see
 * `docs/eng/pilot-react-engine.md`) where it replaces the bespoke
 * scrollY-distance-from-viewport-center scene tracker in
 * `lib/pilot/story.js`. Phases 5 (Sandbox dot stack) and 6 (Pitch dot
 * stack) consume the same hook — so the signature is intentionally
 * generic.
 *
 * Behavior — the "active" section is whichever observed entry is most
 * visible at any given moment. Ties are resolved by document order
 * (first in the `refs` array wins). When no entry is visible (e.g.
 * the user is between sections during a fast scroll) the previous
 * active id is retained until something new becomes visible — this
 * matches the prior story.js feel where the dot didn't blink off
 * during transitions.
 *
 * The hook respects `prefers-reduced-motion` on `scrollTo`; smooth
 * scrolling is opt-out via the user's OS setting, not a per-call flag.
 */

import {useCallback, useEffect, useRef, useState} from 'react';

export interface ScrollSpyTarget {
  id: string;
  ref: React.RefObject<HTMLElement | null>;
}

export interface UseScrollSpyOptions {
  /**
   * IntersectionObserver `rootMargin`. Default narrows the active band
   * to the central 40% of the viewport so the "active" scene is the
   * one centered on screen, not the one merely entering from the
   * bottom. Story / Pitch / Sandbox all want this feel.
   */
  rootMargin?: string;
  /**
   * IntersectionObserver `threshold` — set to a fine-grained ramp so
   * intersectionRatio comparisons are meaningful. Default 0..1 in
   * tenths.
   */
  threshold?: number | number[];
  /**
   * If truthy, the spy is disabled (observers detached, activeId held
   * at its current value). Useful when a parent component is in a
   * transition (e.g. fade-out) and doesn't want the spy to chatter.
   */
  disabled?: boolean;
}

export interface UseScrollSpyResult {
  /** id of whichever target is currently most visible. `null` if none have ever been observed. */
  activeId: string | null;
  /**
   * Smooth-scroll a target into view by id. No-op if the id isn't in
   * the provided targets list or the ref is unmounted. Respects
   * `prefers-reduced-motion`.
   */
  scrollTo: (id: string) => void;
  /**
   * Force the active id directly. Used when a click handler navigates
   * to a target via `scrollTo` and wants the dot to update before the
   * smooth-scroll's IO callbacks catch up. Optional escape hatch — the
   * IO will eventually correct itself.
   */
  setActiveId: (id: string | null) => void;
}

const DEFAULT_OPTIONS: Required<Omit<UseScrollSpyOptions, 'disabled'>> = {
  rootMargin: '-30% 0px -30% 0px',
  threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function useScrollSpy(
  targets: ScrollSpyTarget[],
  options: UseScrollSpyOptions = {},
): UseScrollSpyResult {
  const {rootMargin, threshold} = {...DEFAULT_OPTIONS, ...options};
  const disabled = options.disabled ?? false;

  // Stash the latest targets in a ref so the IO effect (which we
  // re-run only when target identities change) can reach the current
  // list without forcing a re-observe storm.
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const [activeId, setActiveId] = useState<string | null>(() => {
    // Seed with the first target id so the dot stack renders with a
    // sensible selection before the first IO callback fires.
    return targets[0]?.id ?? null;
  });

  // Keep an index of id → latest intersection ratio so we can pick the
  // most-visible target every callback without re-querying the DOM.
  const ratiosRef = useRef<Map<string, number>>(new Map());

  // Recompute observers when the target list identity changes (length
  // or any id rotates). We intentionally don't include `ref` identity
  // changes — refs may legitimately re-mount as React re-renders.
  const targetIds = targets.map((t) => t.id).join('|');

  useEffect(() => {
    if (disabled) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const current = targetsRef.current;
    if (current.length === 0) return;

    const ratios = ratiosRef.current;
    ratios.clear();
    for (const t of current) ratios.set(t.id, 0);

    // Map element → id so the callback can look up which target an
    // observed entry belongs to without walking the array each tick.
    const elementToId = new Map<Element, string>();
    const observed: Element[] = [];

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = elementToId.get(entry.target);
          if (!id) continue;
          ratios.set(id, entry.intersectionRatio);
        }
        // Pick the highest-ratio target. Ties broken by document order
        // via the original targets array.
        let best: string | null = null;
        let bestRatio = -1;
        for (const t of current) {
          const r = ratios.get(t.id) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = t.id;
          }
        }
        // Only publish when something is actually visible. Holding the
        // previous active id while no section is visible prevents the
        // dot from blinking off during fast scrolls / transitions.
        if (bestRatio > 0 && best) {
          setActiveId((prev) => (prev === best ? prev : best));
        }
      },
      {rootMargin, threshold},
    );

    for (const t of current) {
      const el = t.ref.current;
      if (!el) continue;
      elementToId.set(el, t.id);
      observed.push(el);
      io.observe(el);
    }

    return () => {
      io.disconnect();
      elementToId.clear();
    };
  }, [targetIds, rootMargin, threshold, disabled]);

  const scrollTo = useCallback((id: string) => {
    const target = targetsRef.current.find((t) => t.id === id);
    if (!target) return;
    const el = target.ref.current;
    if (!el) return;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  return {activeId, scrollTo, setActiveId};
}
